import {
  ACCESS_TOKEN_KEY,
  FAILED_TO_VERIFY_ERROR,
  INVALID_CODE_ERROR,
  PENDING_BUSINESS_ID,
  REFRESH_TOKEN_KEY,
  SEND_CODE_FAILED_ERROR,
  VERIFICATION_FAILED_ERROR,
} from '@mondaygpt/constants'
import { AccessTokenPayload } from '@mondaygpt/types'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { jwtDecode } from 'jwt-decode'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { apiCall, getDeviceId } from 'modules/core'
import {
  ApiError,
  AuthenticationService,
  BusinessMembersService,
  OpenAPI,
  RefreshTokenDto,
  RequestOtpDto,
  UsersService,
  VerifyOtpDto,
  OAuthService,
  ProviderSigninRequestDto,
  VerifyOtpResponseDto,
} from 'openapi'
import { intercom } from 'services/intercom'
import { useBrandKitStore } from 'modules/creative/store/brand-kit-store'
import { readAttributionCookie } from 'services/funnel-fighters'
import { RootState } from 'types'
import { broadcastLogout } from '../utils/auth-broadcast'

/**
 * Attach the referral code captured on the landing page (`?ref=CODE`) to a
 * signup request body. CRITICAL: this reads the attribution COOKIE, not the
 * current URL — the Google OAuth redirect round-trip drops URL params, so the
 * cookie is the only reliable carrier by the time signup completes. The server
 * DTOs accept an optional `referralCode` the generated types don't carry yet,
 * hence the local intersection type; the field is omitted entirely when no
 * code was captured.
 */
const withReferralCode = <T extends object>(body: T): T & { referralCode?: string } => {
  const referralCode = readAttributionCookie().ref
  return referralCode ? { ...body, referralCode } : body
}

interface OtpState {
  email?: string
  isRequesting: boolean
  isVerifying: boolean
  otpSent: boolean
  error?: string
}

interface State {
  isLoggedIn: boolean
  /**
   * Access token kept in-memory only — never persisted, never written to localStorage.
   * Lost on tab close; restored via silent refresh against the HttpOnly cookie at boot.
   */
  accessToken?: string
  userId?: string
  businessId?: string
  /** Role in the scoped business: 'owner' | 'admin' | 'member' | 'viewer'. */
  businessRole?: string
  requiresBusinessCreation?: boolean
  /** True until the boot-time silent refresh has resolved one way or the other. */
  isHydrating: boolean
  otpState: OtpState
}

const {
  authenticationControllerMeV1,
  authenticationControllerRequestOtpV1,
  authenticationControllerVerifyOtpV1,
  authenticationControllerRefreshTokenV1,
  authenticationControllerLogoutV1,
} = AuthenticationService

const { oAuthControllerSigninV1 } = OAuthService

/**
 * Request an OTP email for authentication
 */
export const requestOtpAction = createAsyncThunk(
  'authentication/requestOtp',
  async (email: string, { rejectWithValue }) => {
    const deviceId = getDeviceId()
    const requestBody: RequestOtpDto = { email, deviceId }
    return await apiCall(authenticationControllerRequestOtpV1, rejectWithValue, requestBody)
  }
)

/**
 * Verify an OTP code to authenticate user
 */
export const verifyOtpAction = createAsyncThunk(
  'authentication/verifyOtp',
  async ({ email, code }: { email: string; code: string }, { rejectWithValue }) => {
    const deviceId = getDeviceId()
    const requestBody: VerifyOtpDto & { referralCode?: string } = withReferralCode({
      email,
      code,
      deviceId,
    })

    try {
      const response = await authenticationControllerVerifyOtpV1(requestBody)
      return response
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(
          error.body || {
            message: error.message || INVALID_CODE_ERROR,
          }
        )
      }
      return rejectWithValue({
        message: error instanceof Error ? error.message : FAILED_TO_VERIFY_ERROR,
      })
    }
  }
)

/**
 * Verify an OTP code to authenticate user
 */
export const providerSigninAction = createAsyncThunk(
  'authentication/providerSignin',
  async ({ provider, dto }: { provider: string; dto: ProviderSigninRequestDto }) => {
    return await oAuthControllerSigninV1(provider, withReferralCode(dto))
  }
)

/**
 * Refresh the authentication token using the HttpOnly cookie. Web clients no longer
 * pass the refresh token in the body — it travels via the cookie set on `/refresh`.
 *
 * The legacy `RefreshTokenDto` shape kept `refreshToken` as a field; we still accept
 * one if a caller hands one over (legacy migration path), otherwise we omit it and
 * the server reads from the cookie.
 */
export const refreshTokenAction = createAsyncThunk(
  'authentication/refreshToken',
  async (
    { refreshToken, businessId }: Partial<RefreshTokenDto> & { businessId: string },
    { rejectWithValue }
  ) =>
    await apiCall(authenticationControllerRefreshTokenV1, rejectWithValue, {
      refreshToken: refreshToken ?? '',
      businessId,
    })
)

/**
 * Boot-time silent refresh. Try to swap the HttpOnly cookie for a fresh access token
 * at app load. If it fails (no cookie / expired / revoked) we land in the logged-out
 * state and the user is sent to login.
 *
 * This also handles a one-time legacy migration: if the user is currently signed in
 * via the old localStorage tokens, we forward the legacy refresh token to the server
 * so it can mint a new pair, set the cookie, and we then wipe the localStorage entries.
 *
 * Deduped via a module-level in-flight promise + cross-tab Web Lock so React
 * StrictMode (which double-fires effects in dev) and overlapping boots from sibling
 * tabs share a single /refresh round-trip. Without this, two concurrent calls hit
 * the server's session-rotation step and one of them 401s — fatal during boot.
 */
let silentRefreshInFlight: Promise<{ accessToken: string }> | null = null

const REFRESH_LOCK_NAME = 'taka-auth-refresh'

async function withRefreshLock<T>(body: () => Promise<T>): Promise<T> {
  const locks = (
    navigator as Navigator & {
      locks?: { request: (name: string, cb: () => Promise<T>) => Promise<T> }
    }
  ).locks
  if (!locks?.request) return body()
  return locks.request(REFRESH_LOCK_NAME, body)
}

export const silentRefreshAction = createAsyncThunk(
  'authentication/silentRefresh',
  async (_: void, { rejectWithValue }) => {
    if (silentRefreshInFlight) {
      try {
        return await silentRefreshInFlight
      } catch (error) {
        if (error instanceof ApiError) return rejectWithValue(error.body)
        return rejectWithValue({ message: 'Silent refresh failed' })
      }
    }

    silentRefreshInFlight = (async () => {
      const legacyRefreshToken = (() => {
        try {
          return localStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined
        } catch {
          return undefined
        }
      })()
      const businessId = (() => {
        try {
          const access = localStorage.getItem(ACCESS_TOKEN_KEY)
          if (!access) return PENDING_BUSINESS_ID
          const decoded = jwtDecode<AccessTokenPayload>(access)
          return decoded.bid || PENDING_BUSINESS_ID
        } catch {
          return PENDING_BUSINESS_ID
        }
      })()

      return withRefreshLock(async () => {
        const result = await authenticationControllerRefreshTokenV1({
          refreshToken: legacyRefreshToken ?? '',
          businessId,
        })
        // Migration done: scrub the legacy localStorage entries so they cannot be
        // exfiltrated by future XSS.
        try {
          localStorage.removeItem(ACCESS_TOKEN_KEY)
          localStorage.removeItem(REFRESH_TOKEN_KEY)
        } catch {
          /* swallow */
        }
        return result
      })
    })()

    try {
      return await silentRefreshInFlight
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.body)
      }
      return rejectWithValue({ message: 'Silent refresh failed' })
    } finally {
      silentRefreshInFlight = null
    }
  }
)

/**
 * Complete business setup after creating a business
 * Selects the newly created business and updates authentication tokens
 */
export const selectBusinessSetupAction = createAsyncThunk(
  'authentication/selectBusinessSetup',
  async ({ businessId }: { businessId: string }, { rejectWithValue }) => {
    try {
      const tokens = await AuthenticationService.authenticationControllerSelectBusinessV1({
        businessId,
        refreshToken: '',
      })
      return tokens
    } catch (error) {
      return rejectWithValue({
        message: error instanceof Error ? error.message : 'Failed to select business',
      })
    }
  }
)

/**
 * Accept a collaboration invite via its emailed token. The server validates the
 * token (existence / expiry / email-match) and returns a fresh token pair scoped
 * to the inviter's business — same shape as select-business — so the client lands
 * directly inside that business. The fulfilled matcher below applies the token.
 * On failure, apiCall rejects with the server error body ({ statusCode, message }),
 * which `.unwrap()` re-throws — the page reads statusCode to distinguish a 403
 * email mismatch (wrong-account screen) from a generic failure.
 */
export const acceptInviteByTokenAction = createAsyncThunk(
  'authentication/acceptInviteByToken',
  async ({ token }: { token: string }, { rejectWithValue }) =>
    apiCall(BusinessMembersService.businessMembershipsControllerAcceptByTokenV1, rejectWithValue, {
      token,
    })
)

/**
 * Fetch current user profile data
 */
export const fetchCurrentUserAction = createAsyncThunk(
  'authentication/me',
  async (_: void, { rejectWithValue }) =>
    await apiCall(authenticationControllerMeV1, rejectWithValue)
)

/**
 * Module-level flag toggled while a logout is in progress. Read by `refreshOnce`
 * (api-error-handler) to abort any 401-driven refresh attempts so they cannot
 * race the logout and resurrect the cookie. Cross-tab logouts arrive via the
 * BroadcastChannel; the receiving tab sets this flag in its own broadcast handler.
 */
export let isLoggingOut = false

export function setLoggingOut(value: boolean): void {
  isLoggingOut = value
}

export const logoutAction = createAsyncThunk(
  'authentication/logoutAction',
  async (options: { broadcast?: boolean } | void, { dispatch }) => {
    const broadcast = options?.broadcast !== false

    // Set the guard FIRST so any concurrent refreshOnce in this tab aborts.
    isLoggingOut = true

    // Broadcast logout immediately so sibling tabs flip their guard.
    if (broadcast) broadcastLogout()

    // CRITICAL: keep the access token in Redux until AFTER /logout returns —
    // /logout requires a valid Bearer and is the only way to set the server-side
    // logout grace marker that prevents racing refreshes from creating a new
    // session that survives the logout. Clearing state too early 401s /logout.
    try {
      await authenticationControllerLogoutV1({ refreshToken: '' })
    } catch {
      /* network failure shouldn't block local cleanup */
    }

    // Pause to let any /refresh that was on the wire when logout fired complete,
    // then a second /logout revokes any session that race-created during the
    // window AND ensures our cookie-clear is the last Set-Cookie the browser
    // processes. Server's grace marker (set by the first /logout) blocks any
    // /refresh that arrives mid-window from creating a new session.
    await new Promise(r => setTimeout(r, 200))
    try {
      await authenticationControllerLogoutV1({ refreshToken: '' })
    } catch {
      /* best-effort */
    }

    // Now safe to clear local state — /logout has set its grace marker and
    // revoked all sessions for the device.
    dispatch(logout())

    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
    } catch {
      /* swallow */
    }

    useBrandKitStore.getState().resetBrandKit()

    const { persistor } = await import('store')
    await persistor.purge()

    isLoggingOut = false
    return { success: true }
  }
)

/**
 * Permanently delete the authenticated user's account.
 *
 * Hits `DELETE /v1/users/account` with the typed-DELETE confirmation, then
 * mirrors `logoutAction`'s local cleanup so the client returns to the
 * unauthenticated state with no stale session data.
 *
 * On 409 (user owns a multi-member business) the server message is rejected
 * verbatim so the page can surface it via toast and keep the user signed in.
 * On 404 we treat the account as already-deleted (the row is gone, the
 * outcome the user asked for is true) and still run the local cleanup so the
 * tab doesn't sit on a stale signed-in shell.
 */
export const deleteAccountAction = createAsyncThunk(
  'authentication/deleteAccountAction',
  async (_: void, { dispatch, rejectWithValue }) => {
    try {
      await UsersService.usersControllerDeleteAccountV1({ confirmation: 'DELETE' })
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const body = error.body as { message?: string } | undefined
        return rejectWithValue({
          status: 409,
          message: body?.message ?? 'Cannot delete account while you own a business with members.',
        })
      }
      if (!(error instanceof ApiError) || error.status !== 404) {
        return rejectWithValue({
          status: error instanceof ApiError ? error.status : 500,
          message: error instanceof Error ? error.message : 'Failed to delete account',
        })
      }
      // 404 falls through to local cleanup: the account is already gone
      // server-side; we still need to drop our local state.
    }

    // Account is gone server-side. Purge local state, mirroring logoutAction
    // but skipping the /logout RPC (server has already revoked sessions and
    // the user row is gone, so /logout would 401). Use try/finally so a
    // rejection in any step still clears the guard flag, otherwise a stuck
    // `isLoggingOut = true` would silently break the next sign-in attempt.
    try {
      isLoggingOut = true
      broadcastLogout()

      dispatch(logout())

      try {
        localStorage.removeItem(ACCESS_TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
      } catch {
        /* swallow */
      }

      useBrandKitStore.getState().resetBrandKit()

      const { persistor } = await import('store')
      await persistor.purge()
    } finally {
      isLoggingOut = false
    }
    return { success: true }
  }
)

/**
 * Set authentication state and update API client. Tokens live in Redux memory only;
 * no localStorage writes here.
 */
const setAuthenticationState = (state: State, accessToken?: string) => {
  const isLoggedIn = !!accessToken
  state.isLoggedIn = isLoggedIn
  state.accessToken = accessToken

  if (accessToken) {
    try {
      const decoded = jwtDecode<AccessTokenPayload>(accessToken)
      state.userId = decoded.sub
      state.businessId = decoded.bid
      state.businessRole = decoded.brole

      // Track businessId changes for debugging FK violations (SERVER-MC/K8)
      if (decoded.bid === PENDING_BUSINESS_ID || !decoded.bid) {
        errorReportingService.addBreadcrumb(
          `businessId set to "${decoded.bid ?? 'undefined'}" from JWT`,
          'auth',
          { userId: decoded.sub }
        )
      }
    } catch (error) {
      console.error('Error decoding JWT token:', error)
      state.isLoggedIn = false
      state.accessToken = undefined
      state.userId = undefined
      state.businessId = undefined
      state.businessRole = undefined
    }
  } else {
    state.userId = undefined
    state.businessId = undefined
    state.businessRole = undefined
  }

  if (isLoggedIn) {
    state.requiresBusinessCreation = state.businessId === PENDING_BUSINESS_ID
  }

  OpenAPI.TOKEN = accessToken
}

// Initial OTP state
const initialOtpState: OtpState = {
  isRequesting: false,
  isVerifying: false,
  otpSent: false,
}

// Initial application state — starts hydrating; silentRefreshAction flips this off.
const initialState: State = {
  isLoggedIn: false,
  isHydrating: true,
  otpState: initialOtpState,
}

const slice = createSlice({
  name: 'authenticationView',
  initialState,
  reducers: {
    /**
     * Marks the slice as hydrated without changing tokens. Used when the caller
     * bypasses silentRefreshAction (e.g., initial render before auth boot completes
     * but after we know there are no tokens at all).
     */
    finishHydration: state => {
      state.isHydrating = false
    },
    syncAccessToken: (state, action: PayloadAction<string>) => {
      // Two guards: (a) skip no-op writes; (b) don't re-authenticate after this
      // tab logged out — sibling broadcasts can race the local logout window.
      if (state.accessToken === action.payload) return
      if (!state.accessToken) return
      setAuthenticationState(state, action.payload)
      state.isHydrating = false
    },
    logout: state => {
      setAuthenticationState(state)
      state.otpState = { ...initialOtpState }
      state.isHydrating = false

      try {
        intercom.logout()
      } catch (error) {
        console.warn('Failed to logout from Intercom:', error)
      }
    },
    clearOtpState: state => {
      state.otpState = { ...initialOtpState }
    },
  },
  extraReducers: builder => {
    // Request OTP cases
    builder.addCase(requestOtpAction.pending, (state, action) => {
      state.otpState.isRequesting = true
      state.otpState.error = undefined
      state.otpState.email = action.meta.arg
    })
    builder.addCase(requestOtpAction.fulfilled, state => {
      state.otpState.isRequesting = false
      state.otpState.otpSent = true
    })
    builder.addCase(
      requestOtpAction.rejected.type,
      (state, action: PayloadAction<{ message?: string } | undefined>) => {
        state.otpState.isRequesting = false
        const errorMessage = action.payload?.message || SEND_CODE_FAILED_ERROR
        state.otpState.error = errorMessage
      }
    )

    // Refresh token case. Guard against the logout-races-refresh window: if the
    // user logged out while the /refresh round-trip was in flight, the response
    // would otherwise re-authenticate them. Skip the write if state was cleared.
    builder.addCase(refreshTokenAction.fulfilled, (state, action) => {
      if (!state.accessToken) return
      setAuthenticationState(state, action.payload.accessToken)
    })

    // Silent refresh on boot — finalizes hydration in either direction
    builder.addCase(silentRefreshAction.fulfilled, (state, action) => {
      const { accessToken } = action.payload
      setAuthenticationState(state, accessToken)
      state.isHydrating = false
    })
    builder.addCase(silentRefreshAction.rejected, state => {
      setAuthenticationState(state)
      state.isHydrating = false
    })

    // Handle business setup completion
    builder.addCase(selectBusinessSetupAction.fulfilled, (state, action) => {
      const { accessToken } = action.payload
      setAuthenticationState(state, accessToken)
      state.otpState = { ...initialOtpState }
    })

    // Delete and leave business endpoints return a fresh token pair scoped to
    // the user's next-best business (or PENDING_BUSINESS_ID if none left).
    // Apply the token so the next API call doesn't 401 against a businessId
    // the user no longer belongs to.
    //
    // Wired via raw action-type matchers (instead of importing the thunks
    // from `modules/business`) to keep this slice independent of the
    // business-entities slice — adding the import would create a circular
    // dependency (business-entities already imports `selectBusinessSetupAction`
    // from here).
    builder.addMatcher(
      action =>
        action.type === 'businesses/deleteCurrent/fulfilled' ||
        action.type === 'businesses/leaveCurrent/fulfilled' ||
        // accept-invite-by-token returns a fresh pair already scoped to the
        // inviter's business, so applying the access token here lands the user
        // inside it without a second select-business round-trip.
        action.type === acceptInviteByTokenAction.fulfilled.type,
      (state, action: PayloadAction<{ accessToken: string }>) => {
        setAuthenticationState(state, action.payload.accessToken)
      }
    )

    builder.addMatcher(
      action =>
        action.type === verifyOtpAction.pending.type ||
        action.type === providerSigninAction.pending.type,
      state => {
        state.otpState.isVerifying = true
        state.otpState.error = undefined
      }
    )

    builder.addMatcher(
      action =>
        action.type === verifyOtpAction.fulfilled.type ||
        action.type === providerSigninAction.fulfilled.type,
      (state, action: PayloadAction<VerifyOtpResponseDto>) => {
        const { tokens, requiresBusinessCreation } = action.payload
        const { accessToken } = tokens
        setAuthenticationState(state, accessToken)
        // setAuthenticationState derives requiresBusinessCreation from
        // `bid === PENDING_BUSINESS_ID` alone, which misses the legacy
        // draft-only case where the server has issued a real (draft) bid but
        // wants the client to route into onboarding. Honor the server's
        // explicit flag when it's truthy so the OnboardingGuard sees it.
        if (requiresBusinessCreation) {
          state.requiresBusinessCreation = true
        }
        state.otpState = { ...initialOtpState }
        state.isHydrating = false
      }
    )

    builder.addMatcher(
      action =>
        action.type === verifyOtpAction.rejected.type ||
        action.type === providerSigninAction.rejected.type,
      (state, action: PayloadAction<{ message?: string } | undefined>) => {
        state.otpState.isVerifying = false
        const errorMessage = action.payload?.message || VERIFICATION_FAILED_ERROR
        state.otpState.error = errorMessage
      }
    )
  },
})

export const authenticationViewReducer = slice.reducer

// Export reducers
export const { finishHydration, logout, clearOtpState, syncAccessToken } = slice.actions

// Selectors
export const selectOtpState = (state: RootState) => state.authenticationView.otpState
export const selectIsHydrating = (state: RootState) => state.authenticationView.isHydrating
export const selectAccessToken = (state: RootState) => state.authenticationView.accessToken
export {
  selectBusinessId,
  selectCurrentBusinessRole,
} from '../../common/selectors/authentication-view.selector'
export const selectRequiresBusinessCreation = (state: RootState) =>
  state.authenticationView.requiresBusinessCreation
